import { useEffect, useState } from 'react'
import { PaperPlaneTilt, Lock, Clock, Users, CalendarBlank, ArrowsClockwise, PaperPlaneRight, CaretRight } from '@phosphor-icons/react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useCampaignApprovalsStore } from '@/stores/campaignApprovalsStore'
import { usePermission } from '@/hooks/usePermission'
import { cardChromeClass, iconChipClass, iconChipAmberClass } from '@/lib/styleKit'
import type { PendingCampaignApproval } from '@/lib/api'
import CampaignReviewModal from './CampaignReviewModal'

const assignedChipClass =
  'inline-flex items-center rounded-full border border-[#5e6ad2]/25 bg-[#5e6ad2]/[0.10] dark:bg-[#5e6ad2]/[0.14] ' +
  'px-2 py-0.5 text-[#5e6ad2] dark:text-[#a5b4fc] flex-shrink-0'

export default function CampaignApprovalsSection() {
  const { pending, loading, fetchPending, approve, reject } = useCampaignApprovalsStore()
  const { allowed, scope } = usePermission('campaigns', 'launch')
  // A Rep's own level for this permission is "gated" (truthy, so `allowed`
  // alone would let them in) -- "gated" means "may request a launch of their
  // own campaign", never "may approve one". Only "all" (Owner/Admin/Manager)
  // may see or act on this queue.
  const canApprove = allowed && scope === 'all'
  const [selected, setSelected] = useState<PendingCampaignApproval | null>(null)

  useEffect(() => {
    if (!canApprove) return
    fetchPending().catch(() => {})
  }, [canApprove, fetchPending])

  const openRow = (req: PendingCampaignApproval) => setSelected(req)

  return (
    <div className="space-y-6">
      <div>
        <h2
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
        >
          Campaign Approvals
        </h2>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-1"
          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
        >
          {canApprove
            ? 'Reps need your approval before a campaign they built can start sending. Open a request to review the audience, sender, signature and workflow, then approve or send it back with a reason.'
            : 'Your role determines what you can see and do in this account.'}
        </p>
      </div>

      {!canApprove ? (
        <Card className={cardChromeClass}>
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className={iconChipAmberClass}>
                <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400" weight="fill" />
              </div>
              <div>
                <CardTitle>No access</CardTitle>
                <CardDescription>
                  Your role doesn't include reviewing campaign launch requests
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      ) : (
        <Card className={cardChromeClass}>
          <CardHeader>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <div className={iconChipClass}>
                  <PaperPlaneTilt className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                </div>
                <div>
                  <CardTitle>Pending requests</CardTitle>
                  <CardDescription>Campaigns a Representative asked to launch</CardDescription>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                loading={loading}
                onClick={() => fetchPending().catch(() => {})}
                icon={<ArrowsClockwise className="w-3.5 h-3.5" weight="regular" />}
                aria-label="Refresh pending requests"
              >
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {pending.length === 0 ? (
              <p
                className="text-muted-foreground dark:text-[#8a8f98]"
                style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
              >
                {loading ? 'Loading…' : 'No pending launch requests.'}
              </p>
            ) : (
              <div className="space-y-2">
                {pending.map((req) => (
                  <div
                    key={req.campaign_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openRow(req)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        openRow(req)
                      }
                    }}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.08] cursor-pointer hover:border-[#5e6ad2]/40 hover:bg-muted/60 dark:hover:bg-white/[0.04] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#5e6ad2] transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="text-foreground dark:text-[#f7f8f8] truncate"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                        >
                          {req.campaign_name}
                        </span>
                        {req.assigned_to_me && (
                          <span className={assignedChipClass} style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.05px' }}>
                            Assigned to you
                          </span>
                        )}
                      </div>
                      {/* Audience size and send time are the two facts an
                          approver is actually signing off on. */}
                      <div
                        className="text-muted-foreground dark:text-[#8a8f98] flex items-center gap-x-2.5 gap-y-1 flex-wrap mt-0.5"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                      >
                        <span className="flex items-center gap-1.5">
                          <Users className="w-3 h-3" />
                          {req.audience_size.toLocaleString()} recipient{req.audience_size === 1 ? '' : 's'}
                        </span>
                        {req.scheduled_start && (
                          <span className="flex items-center gap-1.5">
                            <CalendarBlank className="w-3 h-3" />
                            {new Date(req.scheduled_start).toLocaleString()}
                          </span>
                        )}
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3" />
                          Requested by {req.requested_by_name}
                          {req.requested_at ? ` on ${new Date(req.requested_at).toLocaleString()}` : ''}
                        </span>
                        {req.approver_name && (
                          <span className="flex items-center gap-1.5">
                            <PaperPlaneRight className="w-3 h-3" />
                            Sent to {req.approver_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <CaretRight className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground dark:text-[#8a8f98]" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <CampaignReviewModal
        request={selected}
        onClose={() => setSelected(null)}
        onApprove={async () => {
          if (selected) await approve(selected.campaign_id)
        }}
        onReject={async (reason) => {
          if (selected) await reject(selected.campaign_id, reason)
        }}
      />
    </div>
  )
}
